import {VOLCANO_BUILDUP_S,VOLCANO_ERUPTION_END_S} from '@hyperlight/shared';
/** Localized, connected plume volumes. Included by the existing atmosphere pass
 * so terrain/roofs occlude smoke and a camera can enter it without proxy shells. */
export const VOLCANIC_PLUME_GLSL=/* glsl */`
uniform int uVentCount;
uniform vec3 uVentOrigin[3],uVentUp[3],uVentRight[3],uVentForward[3];
uniform vec4 uVentShape[3],uVentWindPhase[3];
uniform float uVentNoiseTime;
float ventHash(vec3 p){p.y=mod(p.y,128.0);p=fract(p*vec3(.1031,.11369,.13787));p+=dot(p,p.yzx+19.19);return fract((p.x+p.y)*p.z);}
// Tetrahedral interpolation gives broad angular density facets without a stack
// of separately blended polyhedra or visible billboard edges.
float ventNoise(vec3 p){
 vec3 c=floor(p),f=fract(p),a,b;float hi,mid,lo;
 if(f.x>=f.y){if(f.y>=f.z){a=vec3(1,0,0);b=vec3(1,1,0);hi=f.x;mid=f.y;lo=f.z;}
 else if(f.x>=f.z){a=vec3(1,0,0);b=vec3(1,0,1);hi=f.x;mid=f.z;lo=f.y;}
 else{a=vec3(0,0,1);b=vec3(1,0,1);hi=f.z;mid=f.x;lo=f.y;}}
 else{if(f.x>=f.z){a=vec3(0,1,0);b=vec3(1,1,0);hi=f.y;mid=f.x;lo=f.z;}
 else if(f.y>=f.z){a=vec3(0,1,0);b=vec3(0,1,1);hi=f.y;mid=f.z;lo=f.x;}
 else{a=vec3(0,0,1);b=vec3(0,1,1);hi=f.z;mid=f.y;lo=f.x;}}
 return ventHash(c)*(1.0-hi)+ventHash(c+a)*(hi-mid)+ventHash(c+b)*(mid-lo)+ventHash(c+1.0)*lo;
}
float ventActivity(float phase){
 float warning=smoothstep(0.0,${VOLCANO_BUILDUP_S.toFixed(1)},phase)*(1.0-smoothstep(${VOLCANO_ERUPTION_END_S.toFixed(1)},${(VOLCANO_ERUPTION_END_S+28).toFixed(1)},phase));
 float fountain=smoothstep(${VOLCANO_BUILDUP_S.toFixed(1)},${(VOLCANO_BUILDUP_S+3).toFixed(1)},phase)*(1.0-smoothstep(${(VOLCANO_ERUPTION_END_S-8).toFixed(1)},${VOLCANO_ERUPTION_END_S.toFixed(1)},phase));
 return .4*warning+.8*fountain;
}
float ventDensity(vec3 p,vec4 shape,vec4 wind){
 float rise=9.0,age=max(0.0,p.y)/rise;
 float past=mod(wind.z-age+wind.w*2.0,wind.w);
 float emission=ventActivity(past);
 float height=mix(shape.z,shape.y,clamp(emission,0.0,1.0));
 float vertical=smoothstep(-2.0,7.0,p.y)*(1.0-smoothstep(height*.70,height,p.y));
 if(vertical<=0.0)return 0.0;
 // Hot gas rises out of the source and gradually bends with prevailing wind.
 vec2 center=wind.xy*age*smoothstep(0.0,90.0,p.y);
 float width=shape.x+p.y*.23;
 vec3 q=vec3((p.x-center.x)/width,p.y/max(12.0,shape.x*2.0),(p.z-center.y)/width);
 float broad=ventNoise(vec3(q.x*1.6,q.y-uVentNoiseTime*.2,q.z*1.6)+vec3(shape.w,0,shape.w*.31));
 float detail=ventNoise(vec3(q.x*4.0,q.y*2.0-uVentNoiseTime*.4,q.z*4.0)+shape.w);
 float boundary=1.0-length(q.xz)+(.7*broad+.3*detail-.5)*1.12;
 float body=smoothstep(-.07,.16,boundary);
 return body*vertical*(.55+.45*broad)*(.7+emission*.7);
}
vec3 ventLocal(vec3 p,int j){vec3 q=p-uVentOrigin[j];return vec3(dot(q,uVentRight[j]),dot(q,uVentUp[j]),dot(q,uVentForward[j]));}
vec3 ventDirection(vec3 d,int j){return vec3(dot(d,uVentRight[j]),dot(d,uVentUp[j]),dot(d,uVentForward[j]));}
vec2 ventBox(vec3 ro,vec3 rd,vec3 low,vec3 high){
 vec3 safe=mix(vec3(-1.0),vec3(1.0),step(vec3(0.0),rd))*max(abs(rd),vec3(.000001));
 vec3 a=(low-ro)/safe,b=(high-ro)/safe;vec3 lo=min(a,b),hi=max(a,b);
 return vec2(max(max(lo.x,lo.y),lo.z),min(min(hi.x,hi.y),hi.z));
}
vec4 volcanicPlumes(vec3 ro,vec3 rd,float surfaceDistance){
 vec3 L=vec3(0.0);float T=1.0;
 vec2 spans[3];
 for(int j=0;j<3;j++){
  spans[j]=vec2(1e20,-1e20);if(j>=uVentCount)continue;
  vec4 shape=uVentShape[j],wind=uVentWindPhase[j];
  vec3 local=ventLocal(ro,j),ray=ventDirection(rd,j);
  float width=shape.x*1.7+shape.y*.38;
  vec2 bend=wind.xy*shape.y/9.0;
  vec3 lo=vec3(min(0.0,bend.x)-width,-2.0,min(0.0,bend.y)-width);
  vec3 hi=vec3(max(0.0,bend.x)+width,shape.y,max(0.0,bend.y)+width);
  vec2 span=ventBox(local,ray,lo,hi);span.x=max(0.0,span.x);span.y=min(surfaceDistance,span.y);
  if(span.y>span.x)spans[j]=span;
 }
 // Sort actual ray intervals, including when viewing multiple distant vents.
 for(int pass=0;pass<3;pass++){
  int j=0;for(int k=1;k<3;k++)if(spans[k].x<spans[j].x)j=k;
  vec2 span=spans[j];spans[j].x=1e20;if(span.y<=span.x)break;
  vec4 shape=uVentShape[j],wind=uVentWindPhase[j];
  vec3 local=ventLocal(ro,j),ray=ventDirection(rd,j);
  float stepM=(span.y-span.x)/float(VENT_STEPS);
  float jitter=.5; // fixed midpoint: stable while standing and deterministic on seeks
  vec3 sun=ventDirection(uLightDir[0],j);
  vec3 moon=ventDirection(uLightDir[1],j);
  for(int i=0;i<VENT_STEPS;i++) {
   vec3 p=local+ray*(span.x+(float(i)+jitter)*stepM);
   float rho=ventDensity(p,shape,wind);
   if(rho>.0001) {
    float extinction=.016*max(.55,18.0/shape.x);
    float dt=exp(-rho*extinction*stepM);
    float shadow=1.0,moonShadow=1.0;
    if(dot(uLightColor[0],uLightColor[0])>.00001){
     #if VENT_STEPS >= 48
      shadow=exp(-ventDensity(p+sun*shape.x*2.0,shape,wind)*1.5-ventDensity(p+sun*shape.x*5.0,shape,wind)*.55);
     #else
      shadow=exp(-ventDensity(p+sun*shape.x*3.0,shape,wind)*1.9);
     #endif
    }
    if(dot(uLightColor[1],uLightColor[1])>.00001)moonShadow=exp(-ventDensity(p+moon*shape.x*3.0,shape,wind)*1.3);
    vec3 light=uAmbient*1.25*mix(vec3(.65,.85,1.3),vec3(1.0),shadow)+uLightColor[0]*(.10+.78*shadow)+uLightColor[1]*(.10+.55*moonShadow)+uLightColor[2]*.32;
    vec3 pigment=mix(vec3(.48,.56,.68),vec3(.90,.88,.82),shadow);
    float baseGlow=exp(-max(0.0,p.y)/max(18.0,shape.x*2.0))*ventActivity(wind.z)*.45;
    L+=T*(1.0-dt)*(pigment*light+vec3(.95,.10,.005)*baseGlow);
    T*=dt;if(T<.008)break;
   }
  }
 }
 return vec4(L,T);
}
`;
